import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as styles from './styles'

const propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  presetQueries: PropTypes.array.isRequired,
}

const QueryWidget = ({ title, description, presetQueries }) => {
  const [selectedQuery, setSelectedQuery] = useState(presetQueries[0])
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const executeQuery = async (query) => {
    setLoading(true)
    setError(null)
    
    try {
      // Determine endpoint based on query type
      const endpoint = query.endpoint || '_predict'
      const apiUrl = `https://aito-demo.aito.app/api/v1/${endpoint}`
      
      // Start timing the total request
      const startTime = performance.now()
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'x-api-key': 'yg4rTlXkqDzm4y8gPeY75HCKaNwfbTQ2si64ONTi',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(query.payload),
      })
      
      // Calculate total response time
      const totalTime = Math.round(performance.now() - startTime)
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`)
      }
      
      // Extract Aito processing time from response header
      // Note: Header access may be restricted by CORS in production
      let aitoResponseTime = null
      let queryTime = null
      let latency = null
      
      try {
        aitoResponseTime = response.headers.get('x-aitoai-response-time')
        queryTime = aitoResponseTime ? parseInt(aitoResponseTime) : null
        latency = queryTime ? totalTime - queryTime : null
      } catch (e) {
        // Header access might be blocked by CORS policy in production
        console.log('Could not access response headers (CORS restriction)')
      }
      
      const data = await response.json()
      setResult({ 
        ...data, 
        endpoint,
        timing: {
          total: totalTime,
          query: queryTime,
          latency: latency
        }
      })
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleQuerySelect = (query) => {
    setSelectedQuery(query)
    setResult(null)
    setError(null)
  }

  const formatProbability = (prob) => {
    return (prob * 100).toFixed(1) + '%'
  }

  return (
    <styles.Container>
      <styles.Header>
        <styles.Title>{title}</styles.Title>
        <styles.Description>{description}</styles.Description>
      </styles.Header>

      <styles.QuerySection>
        <styles.QueryTabs>
          {presetQueries.map((query, index) => (
            <styles.QueryTab
              key={index}
              active={selectedQuery.id === query.id}
              onClick={() => handleQuerySelect(query)}
            >
              {query.name}
            </styles.QueryTab>
          ))}
        </styles.QueryTabs>

        <styles.QueryDisplay>
          <styles.QueryTitle>{selectedQuery.name}</styles.QueryTitle>
          <styles.QueryDescription>{selectedQuery.description}</styles.QueryDescription>
          
          <styles.CodeBlock>
            <styles.CodeHeader>
              <styles.CodeLanguage>Aito Query</styles.CodeLanguage>
              <styles.RunButton 
                onClick={() => executeQuery(selectedQuery)}
                disabled={loading}
              >
                {loading ? 'Running...' : 'Run Query'}
              </styles.RunButton>
            </styles.CodeHeader>
            <styles.Code>
              {JSON.stringify(selectedQuery.payload, null, 2)}
            </styles.Code>
          </styles.CodeBlock>
        </styles.QueryDisplay>
      </styles.QuerySection>

      {loading && (
        <styles.LoadingSection>
          <styles.LoadingSpinner />
          <styles.LoadingText>Executing query...</styles.LoadingText>
        </styles.LoadingSection>
      )}

      {error && (
        <styles.ErrorSection>
          <styles.ErrorText>Error: {error}</styles.ErrorText>
        </styles.ErrorSection>
      )}

      {result && (
        <styles.ResultsSection>
          <styles.ResultsHeader>
            <styles.ResultsTitle>
              {result.endpoint === '_predict' ? 'Prediction Results' : 
               result.endpoint === '_recommend' ? 'Recommendations' : 'Query Results'}
            </styles.ResultsTitle>
            <styles.ResultsCount>
              {result.total || result.hits?.length || 0} results found
            </styles.ResultsCount>
          </styles.ResultsHeader>
          
          {result.timing && result.timing.query && result.timing.latency && (
            <styles.TimingDisplay>
              {result.timing.total}ms response = {result.timing.query}ms query + {result.timing.latency}ms latency
            </styles.TimingDisplay>
          )}
          
          <styles.ResultsList>
            {(result.hits || []).slice(0, 5).map((hit, index) => {
              // Handle different response structures
              let displayValue, confidence
              
              if (result.endpoint === '_query' && hit.$value) {
                displayValue = hit.$value
                confidence = hit.$p
              } else if (result.endpoint === '_recommend' && hit[selectedQuery.predictField]) {
                displayValue = hit[selectedQuery.predictField]
                confidence = hit.$p
              } else if (hit[selectedQuery.predictField]) {
                displayValue = hit[selectedQuery.predictField]
                confidence = hit.$p
              } else {
                displayValue = Object.values(hit).find(v => typeof v === 'string' && v !== hit.$p)
                confidence = hit.$p
              }
              
              return (
                <styles.ResultItem key={index} rank={index + 1}>
                  <styles.ResultRank>#{index + 1}</styles.ResultRank>
                  <styles.ResultContent>
                    <styles.ResultValue>
                      {displayValue || 'Result'}
                    </styles.ResultValue>
                    <styles.ResultDetails>
                      {Object.entries(hit)
                        .filter(([key, value]) => 
                          key !== '$p' && 
                          key !== '$value' && 
                          key !== selectedQuery.predictField &&
                          typeof value !== 'object'
                        )
                        .slice(0, 2)
                        .map(([key, value]) => (
                          <styles.ResultDetail key={key}>
                            {key}: {value}
                          </styles.ResultDetail>
                        ))}
                    </styles.ResultDetails>
                  </styles.ResultContent>
                  {confidence && (
                    <styles.ProbabilityBadge confidence={confidence}>
                      {formatProbability(confidence)}
                    </styles.ProbabilityBadge>
                  )}
                </styles.ResultItem>
              )
            })}
          </styles.ResultsList>
        </styles.ResultsSection>
      )}
    </styles.Container>
  )
}

QueryWidget.propTypes = propTypes

export default QueryWidget